"use client";

import { forwardRef, useState, useRef, useEffect } from "react";
import type { CSSProperties, ReactNode } from "react";
import type { StaticImageData } from "next/image";
import Image from "next/image";
import { cn } from "@/shared/lib/utils";
import { AccentStar } from "@/shared/ui/accent-star";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SectionBlock } from "./SectionBlock";
import { useScrollReveal, type RevealState } from "@/shared/lib/useScrollReveal";

/* ─────────────────────────────────────────────────────────────
   Публичные типы
───────────────────────────────────────────────────────────── */
export interface FaqItem {
  question: string;
  answer: string;
}

export interface TeamMember {
  name: string;
  role: string;
  initials: string;
  /** Опциональная фотография — StaticImageData (import) или url-строка */
  photo?: StaticImageData | string;
}

export interface ContactLink {
  icon: ReactNode;
  title: string;
  value: string;
  href: string;
}

export interface PageFooterSectionProps {
  blockId?: string;
  faqs?: FaqItem[];
  teamMembers?: TeamMember[];
  teamLabel?: string;
  contactLinks?: ContactLink[];
  accentColor?: string;
}

/* ─────────────────────────────────────────────────────────────
   Анимации
───────────────────────────────────────────────────────────── */
function fromTop(state: RevealState): CSSProperties {
  if (state === "done") return {};
  if (state === "idle") return { opacity: 0, transform: "translateY(-2.5rem)" };
  return {
    opacity: 0,
    animation:
      "slide-from-top 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0s forwards",
  };
}

function fromBottom(state: RevealState, delay: number): CSSProperties {
  if (state === "done") return {};
  if (state === "idle") return { opacity: 0, transform: "translateY(2.5rem)" };
  return {
    opacity: 0,
    animation: `slide-from-bottom 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay.toFixed(2)}s forwards`,
  };
}

function fadePop(state: RevealState, delay: number): CSSProperties {
  if (state === "done") return {};
  if (state === "idle") return { opacity: 0, transform: "scale(0.88)" };
  return {
    opacity: 0,
    animation: `fade-scale-in 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay.toFixed(2)}s forwards`,
  };
}

/* ─────────────────────────────────────────────────────────────
   Компонент

   Возвращает React Fragment (<>) с двумя независимыми <section>:
     1. FAQ-секция  — белый SectionBlock, двухпанельный Q&A без прыжков
     2. Контакты-секция — цветной блок с командой + ссылками

   ref прокинут в Contacts-секцию.
───────────────────────────────────────────────────────────── */
export const PageFooterSection = forwardRef<
  HTMLElement,
  PageFooterSectionProps
>(
  (
    {
      blockId = "footer",
      faqs = [],
      teamMembers = [],
      teamLabel = "Наша команда",
      contactLinks = [],
      accentColor = "var(--help)",
    },
    ref,
  ) => {
    const currentYear = new Date().getFullYear();
    const { ref: revealRef, state } = useScrollReveal(blockId, 0.1);

    /* Активный вопрос — стейт панели FAQ */
    const [activeQ, setActiveQ] = useState(0);

    /* Стейт для карусели команды */
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const carouselRef = useRef<HTMLDivElement>(null);

    const handleScroll = () => {
      if (carouselRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
        setCanScrollLeft(scrollLeft > 5);

        const firstCard = carouselRef.current.children[0] as HTMLElement;
        const secondCard = carouselRef.current.children[1] as HTMLElement;
        if (firstCard && secondCard) {
          const cardWidth = secondCard.offsetLeft - firstCard.offsetLeft;
          const index = Math.round(scrollLeft / cardWidth);
          setCurrentIndex(index);

          const totalCards = carouselRef.current.children.length;
          setCanScrollRight(
            index < totalCards - 1 &&
              Math.ceil(scrollLeft + clientWidth) < scrollWidth - 40,
          );
        } else if (firstCard) {
          setCanScrollRight(
            Math.ceil(scrollLeft + clientWidth) < scrollWidth - 40,
          );
        } else {
          setCanScrollRight(false);
        }
      }
    };

    useEffect(() => {
      handleScroll();
    }, []);

    // revealRef — на FAQ секцию когда есть FAQ, иначе на Контакты
    const setContactsRef = (el: HTMLElement | null) => {
      if (faqs.length === 0) revealRef(el);
      if (typeof ref === "function") ref(el);
      else if (ref)
        (ref as React.MutableRefObject<HTMLElement | null>).current = el;
    };


    return (
      <>

        {/* ══════════════════════════════════════════════════════
            СЕКЦИЯ 1: Частые вопросы
            Самостоятельная <section>.
            Двухпанельный layout: список слева, ответ справа.
            CSS grid stacking на ответах → высота = max-answer → 0 прыжков.
        ══════════════════════════════════════════════════════ */}
        {faqs.length > 0 && (
          <section ref={(el) => { revealRef(el); }} className="flex flex-col">
            <SectionBlock
              color={accentColor}
              className="flex flex-col gap-fluid-xl p-fluid-xl"
            >
              {/* Заголовок */}
              <div className="overflow-hidden">
                <div style={fromTop(state)} className="text-left">
                  <h2 className="font-content font-semibold text-fluid-4xl leading-tight text-foreground block select-none">
                    Ответы на{" "}
                    <span style={{ color: accentColor }}>частые вопросы</span>
                  </h2>
                  <p className="font-content font-light text-fluid-xl leading-relaxed text-foreground/60 mt-fluid-sm max-w-[65ch] block select-none">
                    Всё, что обычно хочется уточнить до начала работы
                  </p>
                  <div className="border-t border-border mt-fluid-lg" />
                </div>
              </div>

              {/*
                Двухпанельный FAQ:
                  Левая панель — кликабельный список вопросов
                  Правая панель — CSS grid stacking ответов:
                    все ответы в DOM → высота = наибольший → НУЛЕВЫЕ ПРЫЖКИ
              */}
              {/*
                ── МОБИЛКА (< lg): стандартный аккордеон ──
                Ответ открывается прямо под вопросом, сдвигая остальные.
              */}
              <div
                style={fromBottom(state, 0.2)}
                className="lg:hidden"
              >
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, i) => (
                    <div key={i} style={fromBottom(state, 0.22 + i * 0.04)}>
                      <AccordionItem value={`m-${i}`} className="border-border">
                        <AccordionTrigger
                          starColor={accentColor}
                          className="text-left px-fluid-lg font-content text-fluid-base text-foreground lg:hover:no-underline py-fluid-md"
                        >
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="px-fluid-lg font-content text-fluid-base text-foreground/70 leading-relaxed">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    </div>
                  ))}
                </Accordion>
              </div>

              {/*
                ── ДЕСКТОП (>= lg): split-panel ──
                Без внешней рамки — только внутренний divider между колонками.
                CSS grid stacking → высота фиксирована → нулевые прыжки.
              */}
              <div
                style={fromBottom(state, 0.2)}
                className="hidden lg:grid lg:grid-cols-[2fr_3fr]"
              >
                {/* Список вопросов */}
                <div className="flex flex-col divide-y divide-border border-r border-border pr-fluid-xl">
                  {faqs.map((faq, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveQ(i)}
                      className={cn(
                        "text-left py-fluid-sm font-content text-fluid-base transition-colors leading-snug",
                        "flex items-center gap-fluid-sm w-full",
                        i === activeQ
                          ? "text-foreground font-medium"
                          : "text-foreground hover:text-foreground/70",
                      )}
                    >
                      <AccentStar
                        color={accentColor}
                        size={16}
                        className={cn(
                          "shrink-0 transition-transform duration-500 ease-in-out",
                          i === activeQ
                            ? "rotate-[315deg] scale-125"
                            : "rotate-0 scale-100 opacity-50",
                        )}
                      />
                      <span>{faq.question}</span>
                    </button>
                  ))}
                </div>

                {/* Панель ответов — grid stacking */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr" }}>
                  {faqs.map((faq, i) => {
                    const isActive = i === activeQ;
                    return (
                      <div
                        key={i}
                        style={{
                          gridColumn: 1,
                          gridRow: 1,
                          visibility: isActive ? "visible" : "hidden",
                        }}
                        className="pl-fluid-xl"
                        aria-hidden={!isActive}
                      >
                        <div
                          key={`d-${activeQ === i ? activeQ : i}`}
                          style={
                            isActive
                              ? {
                                  opacity: 0,
                                  animation:
                                    "slide-from-right-far 0.3s cubic-bezier(0.25,0.46,0.45,0.94) forwards",
                                }
                              : undefined
                          }
                        >
                          <p
                            className="font-heading font-semibold text-fluid-xl leading-snug mb-fluid-md"
                            style={{ color: accentColor }}
                          >
                            {faq.question}
                          </p>
                          <p className="font-content text-fluid-base text-foreground/70 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </SectionBlock>
          </section>
        )}

      {/* ══════════════════════════════════════════════════════
            СЕКЦИЯ 2: Контакты
        ══════════════════════════════════════════════════════ */}
        <section
          ref={setContactsRef}
          className="flex flex-col rounded-[var(--fluid-radius-lg)] overflow-hidden"
          style={{ backgroundColor: accentColor }}
        >
          {/* Заголовок */}
          <div className="relative z-10 p-fluid-xl pb-fluid-lg">
            <div style={fromTop(state)}>
              <h2 className="font-content font-semibold text-fluid-4xl leading-tight text-white block select-none">
                Команда{" "}
                <span className="text-white/70">и контакты</span>
              </h2>
              <p className="font-content font-light text-fluid-xl leading-relaxed text-white/75 block select-none mt-fluid-sm max-w-[65ch]">
                Мы всегда на связи и открыты к вопросам и предложениям
              </p>
              <div className="border-t border-white/20 mt-fluid-lg" />
            </div>
          </div>

          {teamMembers.length > 0 && (() => {
            const isFew = teamMembers.length <= 3;

            return (
              <div
                style={fromBottom(state, 0.28)}
                className="mx-fluid-xl mb-fluid-xl bg-white rounded-[var(--fluid-radius-lg)] relative flex flex-col"
              >
                {/* Пагинатор (стрелки) для мобилки */}
                <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-0 flex justify-between items-center z-30 md:hidden pointer-events-none">
                  <div className="relative translate-x-[-50%]">
                    <button
                      onClick={() => {
                        if (carouselRef.current) {
                          if (currentIndex <= 1) {
                            setCanScrollLeft(false);
                          }
                          const prevIndex = Math.max(currentIndex - 1, 0);
                          const targetCard = carouselRef.current.children[prevIndex] as HTMLElement;
                          const firstCard = carouselRef.current.children[0] as HTMLElement;
                          if (targetCard && firstCard) {
                            const targetScroll = targetCard.offsetLeft - firstCard.offsetLeft;
                            carouselRef.current.scrollTo({
                              left: targetScroll,
                              behavior: "smooth",
                            });
                          }
                        }
                      }}
                      disabled={!canScrollLeft}
                      className={`w-10 h-10 rounded-full border border-border/50 flex items-center justify-center text-foreground bg-white shadow-sm active:bg-accent transition-all pointer-events-auto shrink-0 ${canScrollLeft ? "opacity-100 visible" : "opacity-0 invisible"}`}
                      aria-label="Предыдущий"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m15 18-6-6 6-6" />
                      </svg>
                    </button>
                  </div>
                  <div className="relative translate-x-[50%]">
                    <button
                      onClick={() => {
                        if (carouselRef.current) {
                          const totalCards = carouselRef.current.children.length;
                          if (currentIndex >= totalCards - 2) {
                            setCanScrollRight(false);
                          }
                          const nextIndex = Math.min(currentIndex + 1, totalCards - 1);
                          const targetCard = carouselRef.current.children[nextIndex] as HTMLElement;
                          const firstCard = carouselRef.current.children[0] as HTMLElement;
                          if (targetCard && firstCard) {
                            const targetScroll = targetCard.offsetLeft - firstCard.offsetLeft;
                            carouselRef.current.scrollTo({
                              left: targetScroll,
                              behavior: "smooth",
                            });
                          }
                        }
                      }}
                      disabled={!canScrollRight}
                      className={`w-10 h-10 rounded-full border border-border/50 flex items-center justify-center text-foreground bg-white shadow-sm active:bg-accent transition-all pointer-events-auto shrink-0 ${canScrollRight ? "opacity-100 visible" : "opacity-0 invisible"}`}
                      aria-label="Следующий"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Заголовок группы */}
                <h3
                  style={{ ...fromBottom(state, 0.2), color: accentColor }}
                  className="px-fluid-xl pt-fluid-xl pb-fluid-md font-heading text-fluid-xl leading-tight select-none"
                >
                  {teamLabel}
                </h3>

                {/* Карусель / Грид */}
                <div className="relative group/carousel w-full">
                  <div
                    ref={carouselRef}
                    onScroll={handleScroll}
                    className={cn(
                      "relative z-10 flex [scrollbar-width:none] [-ms-overflow-style:none]",
                      "overflow-x-auto snap-x [scroll-snap-type:x_mandatory] scroll-smooth",
                      "pb-fluid-xl",
                      // Мобилка
                      "[container-type:inline-size] [gap:4cqw] px-[4cqw] [scroll-snap-type:x_mandatory]",
                      // Десктоп
                      "lg:[container-type:normal] lg:gap-tile lg:px-fluid-xl lg:overflow-x-auto",
                      // Центрируем, если карточек <= 3, иначе прижимаем к левому краю
                      isFew ? "lg:justify-center" : "lg:justify-start"
                    )}
                  >
                    {teamMembers.map((m, i) => (
                      <div
                        key={i}
                        style={{ ...fadePop(state, 0.38) }}
                        className={cn(
                          "flex-shrink-0 self-start [scroll-snap-align:start]",
                          "flex flex-col overflow-hidden rounded-[var(--fluid-radius-lg)]",
                          // Мобилка: ширина в cqw
                          "w-[76cqw]",
                          // Десктоп: жестко фиксируем ширину под "3.2 видимых карточки"
                          "lg:w-[calc((100%_-_var(--gap-tile)_*_2)_/_3.2)]"
                        )}
                      >
                        {/* Контейнер фото. Убран bg-black/5, поставлен bg-transparent */}
                        <div className="relative w-full aspect-[2/3] overflow-hidden rounded-[var(--fluid-radius-lg)] bg-transparent">
                          {m.photo ? (
                            <Image
                              src={m.photo}
                              alt={m.name}
                              fill
                              className="object-cover object-top"
                              sizes="(max-width: 768px) 76vw, 33vw"
                            />
                          ) : (
                            <div
                              className="w-full h-full flex items-end justify-start pb-8 pl-fluid-sm"
                              style={{ backgroundColor: `${accentColor}33` }}
                            >
                              <span
                                className="font-heading font-bold text-white/15 leading-none select-none"
                                style={{ fontSize: "clamp(3rem, 15cqw, 6rem)" }}
                              >
                                {m.initials}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="px-fluid-sm py-2 flex flex-col gap-0.5 bg-transparent">
                          <div className="font-heading font-semibold text-fluid-base text-foreground leading-tight">
                            {m.name}
                          </div>
                          <div className="font-content text-fluid-xs text-foreground/70 leading-snug">
                            {m.role}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Контактные ссылки */}
          {contactLinks.length > 0 && (
            <div
              className="relative z-10 px-fluid-xl pb-fluid-xl flex flex-col gap-fluid-md border-t border-white/20 pt-fluid-lg"
              style={{ backgroundColor: accentColor }}
            >
              <p
                style={fromBottom(state, 0.55)}
                className="font-heading text-fluid-xs text-white/50 uppercase tracking-widest"
              >
                Свяжитесь с нами
              </p>
              <div className="flex flex-wrap gap-fluid-md lg:gap-fluid-lg">
                {contactLinks.map((item, i) => (
                  <a
                    key={i}
                    href={item.href}
                    style={fadePop(state, 0.62 + i * 0.07)}
                    className="flex items-center gap-fluid-sm text-white/70 lg:hover:text-white transition-colors"
                  >
                    <span className="shrink-0 [&>svg]:w-5 [&>svg]:h-5 text-white/60">
                      {item.icon}
                    </span>
                    <div className="flex flex-col min-w-0">
                      <span className="font-heading text-fluid-xs text-white/40 leading-none mb-0.5">
                        {item.title}
                      </span>
                      <span className="font-content text-fluid-sm text-white/85 leading-snug whitespace-nowrap">
                        {item.value}
                      </span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Футер-бар — зелёный фон */}
          <div
            style={{ ...fromBottom(state, 1.0), backgroundColor: accentColor }}
            className="relative z-10 px-fluid-xl pb-fluid-xl pt-fluid-md border-t border-white/20 flex flex-col md:flex-row justify-between items-center gap-y-fluid-md gap-x-fluid-lg text-center md:text-left"
          >
            <div className="flex flex-col md:flex-row items-center gap-x-fluid-lg gap-y-fluid-sm">
              <span className="font-heading text-fluid-base text-white/80">
                Practicum &mdash; {currentYear}
              </span>
              <div className="flex flex-wrap justify-center items-center gap-x-fluid-md">
                <a
                  href="/policy"
                  className="font-heading text-fluid-base text-white/70 lg:hover:text-white transition-colors"
                >
                  Политика конфиденциальности
                </a>
                <a
                  href="/offer"
                  className="font-heading text-fluid-base text-white/70 lg:hover:text-white transition-colors"
                >
                  Публичная оферта
                </a>
                <a
                  href="/requisites"
                  className="font-heading text-fluid-base text-white/70 lg:hover:text-white transition-colors"
                >
                  Реквизиты
                </a>
              </div>
            </div>
          </div>

        </section>
      </>
    );
  },
);

PageFooterSection.displayName = "PageFooterSection";